import queryClient from '@src/app/providers/queryClient';
import type { APIClient } from '@src/shared/api';
import { formatIsoDate, subtractDay } from '@src/shared/lib';
import { useMutation, useQuery } from '@src/shared/query';
import { urlToQueryKey } from '@src/shared/query/utils';
import { selectAbsences } from '../../lib/absence';
import type { Absence, AbsenceDto } from '../../types';
import {
  absenceApproveHrUrl,
  absenceApproveUrl,
  absenceDeleteUrl,
  absenceRejectHrUrl,
  absenceRejectUrl,
  absenceRequestUrl,
  absenceUrl,
  groupStatsUrl,
  vacationBalanceUrl,
} from './constants';

export function invalidateAbsenceQueries() {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(absenceUrl)],
    }),
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(groupStatsUrl)],
    }),
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(vacationBalanceUrl)],
    }),
  ]);
}

export type AbsenceQueryParams = Partial<{
  date_from: string;
  date_to: string;
  user_id: string;
}>;

type AbsenceQueryRange = {
  start: Date;
  end: Date;
};

export function getAbsenceQueryParams({
  start,
  end,
}: AbsenceQueryRange): AbsenceQueryParams {
  return {
    date_from: formatIsoDate(subtractDay(start, 1)),
    date_to: formatIsoDate(end),
  };
}

export default function useAbsences(queryParams: AbsenceQueryParams = {}) {
  const query = useQuery<AbsenceDto[], Absence[]>(absenceUrl, {
    queryParams,
    select: selectAbsences,
    initialData: [],
    placeholderData: (previousData) => previousData,
  });

  const { mutateAsync: bookAbsence } = useMutation<
    { absences: Absence[] },
    Error,
    Record<string, unknown> & { headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      headers,
      ...rest
    }: { apiClient: APIClient } & Record<string, unknown>) => {
      return apiClient.request({
        url: absenceUrl,
        method: 'POST',
        headers: headers as Record<string, string> | undefined,
        payload: rest,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: deleteAbsences } = useMutation<
    { absences: Absence[] },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceDeleteUrl,
        method: 'DELETE',
        headers,
        payload: ids,
        bodyOnDelete: true,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: requestAbsences } = useMutation<
    { requested: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceRequestUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ requested: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveAbsences } = useMutation<
    { approved: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceApproveUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ approved: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: rejectAbsences } = useMutation<
    { rejected: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceRejectUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ rejected: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveHrAbsences } = useMutation<
    { approved: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceApproveHrUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ approved: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: rejectHrAbsences } = useMutation<
    { rejected: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceRejectHrUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ rejected: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { data: absences = [], ...restQuery } = query;
  return {
    absences,
    bookAbsence,
    deleteAbsences,
    requestAbsences,
    approveAbsences,
    rejectAbsences,
    approveHrAbsences,
    rejectHrAbsences,
    ...restQuery,
  };
}
